import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  credentialSchema,
  type CreateCredential,
  type Credential,
  type CredentialService,
  type UpdateCredential,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';

export const credentialsKey = ['credentials'] as const;
export const nodeCredentialsKey = (nodeId: string) => ['node-credentials', nodeId] as const;

/** Список без секретов: секрет не отдаётся наружу ни одним маршрутом (§3.3). */
export function useCredentials(enabled = true) {
  return useQuery({
    queryKey: credentialsKey,
    enabled,
    queryFn: () => apiRequest('/credentials', z.array(credentialSchema)),
  });
}

export function useCreateCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCredential) =>
      apiRequest<Credential>('/credentials', credentialSchema, { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: credentialsKey }),
  });
}

/**
 * Правка учётных данных.
 *
 * Секрет передаётся, только если его набрали заново: пустое поле означает
 * «не трогать», а не «стереть». Стереть секрет нельзя вовсе — запись
 * без секрета неотличима от «пароль не задан», а это разные вещи.
 */
export function useUpdateCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCredential }) =>
      apiRequest<Credential>(`/credentials/${id}`, credentialSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: credentialsKey }),
  });
}

export function useDeleteCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentialId: string) =>
      apiRequest(`/credentials/${credentialId}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: credentialsKey }),
  });
}

/** Что привязано к узлу — по одной записи на службу. */
export function useNodeCredentials(nodeId: string) {
  return useQuery({
    queryKey: nodeCredentialsKey(nodeId),
    queryFn: () =>
      // Ключ — служба (`vnc`, `rtsp`, `snmp`, `ssh`), поэтому не массив, а словарь:
      // у узла ровно одна запись на службу, и искать её перебором незачем.
      apiRequest(`/nodes/${nodeId}/credentials`, z.record(z.string(), credentialSchema)),
  });
}

export function useAttachCredential(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, credentialId }: { service: CredentialService; credentialId: string }) =>
      apiRequest(`/nodes/${nodeId}/credentials/${service}`, z.unknown(), {
        method: 'PUT',
        body: { credentialId },
        acceptStatuses: [204],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: nodeCredentialsKey(nodeId) });
      // Счётчик привязок в общем списке тоже изменился.
      void queryClient.invalidateQueries({ queryKey: credentialsKey });
    },
  });
}

export function useDetachCredential(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (service: CredentialService) =>
      apiRequest(`/nodes/${nodeId}/credentials/${service}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: nodeCredentialsKey(nodeId) });
      void queryClient.invalidateQueries({ queryKey: credentialsKey });
    },
  });
}
